import { useState, useEffect, useCallback } from 'react';

/**
 * useSignInPopup - Custom hook to manage sign-in popup display logic
 * 
 * Logic:
 * - Show popup after 10 seconds on initial page visit (for non-authenticated users)
 *   unless options.skipAutoShow is true (e.g. on recipe details: only on button clicks)
 * - Show popup every 40 seconds after that until user signs in (also skipped when skipAutoShow)
 * - Don't show if user is authenticated
 * 
 * @param {boolean} isAuthenticated - Whether the user is logged in
 * @param {string} pageKey - Unique key for the page (e.g., 'searchrecipes', 'recipedetails')
 * @param {{ skipAutoShow?: boolean }} [options] - skipAutoShow: do not show popup on a timer; only when openPopup() is called
 * @returns {object} { showPopup, handleClosePopup, openPopup }
 */
export const useSignInPopup = (isAuthenticated, pageKey, options = {}) => {
    const { skipAutoShow = false } = options;
    const [showPopup, setShowPopup] = useState(false);
    const [hasShownInitial, setHasShownInitial] = useState(false);

    const handleClosePopup = useCallback(() => {
        setShowPopup(false);
    }, []);

    const openPopup = useCallback(() => {
        setShowPopup(true);
    }, []);

    useEffect(() => {
        // Don't show popup if user is authenticated or if auto-show is disabled for this page
        if (isAuthenticated || skipAutoShow) {
            return;
        }

        let initialTimeout;
        let recurringInterval;

        // Show popup after 10 seconds (initial)
        if (!hasShownInitial) {
            initialTimeout = setTimeout(() => {
                setShowPopup(true);
                setHasShownInitial(true);
            }, 10000);
        }

        // After the initial popup, show it every 40 seconds
        if (hasShownInitial) {
            recurringInterval = setInterval(() => {
                setShowPopup(true);
            }, 40000);
        }

        // Cleanup timers
        return () => {
            if (initialTimeout) clearTimeout(initialTimeout);
            if (recurringInterval) clearInterval(recurringInterval);
        };
    }, [isAuthenticated, hasShownInitial, skipAutoShow]);

    // Reset when navigating away (component unmount)
    useEffect(() => {
        return () => {
            setHasShownInitial(false);
            setShowPopup(false);
        };
    }, [pageKey]);

    return { showPopup, handleClosePopup, openPopup };
};
